import { useEffect, useRef, useState } from "react";

import type { Client, ClientState, PusherConfig } from "~/lib/matrix";
import * as platform from "~/lib/platform";
import type { AndroidPushEndpoint as PlatformAndroidPushEndpoint } from "~/lib/platform";

export type { AndroidPushEndpoint as AndroidPushEndpoint } from "~/lib/platform";
export type AndroidPushEvent = { instance: string; roomId: string; eventId: string };

export const ANDROID_APP_ID = "com.nurion.chat";
const ANDROID_DEVICE_NAME = "Android";
const APP_DISPLAY_NAME = "Nurion Chat";

export const GENERIC_PUSH_TITLE = "Nurion Chat";
export const GENERIC_PUSH_BODY = "New message";

export const NURION_NTFY_HOSTS: ReadonlySet<string> = new Set(["ntfy.nurion.tech"]);

const RETRY_DELAYS_MS = [1_000, 5_000, 30_000] as const;

export interface RegisteredAndroidPusher {
  instance: string;
  appId: string;
  pushkey: string;
}

export type AndroidAttemptOutcome =
  | "registered"
  | "denied"
  | "noDistributor"
  | "network"
  | "cancelled"
  | "unsupported";

export type AndroidPushRegistrationOutcome =
  | "registered"
  | "denied"
  | "noDistributor"
  | "network"
  | "unsupported";

export function isRetryable(outcome: AndroidAttemptOutcome): boolean {
  return outcome === "network";
}

export function androidGatewayUrl(endpoint: string): string | null {
  let url: URL;
  try {
    url = new URL(endpoint);
  } catch {
    return null;
  }
  if (url.protocol !== "https:") return null;
  return `${url.origin}/_matrix/push/v1/notify`;
}

function isValidUpTopicPath(pathname: string): boolean {
  if (pathname.length < 3 || !pathname.startsWith("/up")) return false;
  const parts = pathname.slice(1).split("/");
  if (parts.length !== 1) return false;
  const topic = parts[0];
  if (!topic) return false;
  const rest = topic.slice(2);
  if (rest.length === 0) return false;
  return /^[A-Za-z0-9._~-]+$/.test(rest);
}

export function validateAndroidEndpoint(
  endpoint: string,
  allowedHosts: ReadonlySet<string>,
): boolean {
  let url: URL;
  try {
    url = new URL(endpoint);
  } catch {
    return false;
  }
  if (url.protocol !== "https:") return false;
  if (!allowedHosts.has(url.hostname)) return false;
  if (url.username !== "" || url.password !== "") return false;
  if (url.search !== "" || url.hash !== "") return false;
  return isValidUpTopicPath(url.pathname);
}

export function buildAndroidPusherConfig(
  endpoint: string,
  allowedHosts: ReadonlySet<string>,
): PusherConfig | null {
  if (!validateAndroidEndpoint(endpoint, allowedHosts)) return null;
  const gatewayUrl = androidGatewayUrl(endpoint);
  if (!gatewayUrl) return null;
  return {
    appId: ANDROID_APP_ID,
    pushkey: endpoint,
    gatewayUrl,
    appDisplayName: APP_DISPLAY_NAME,
    deviceDisplayName: ANDROID_DEVICE_NAME,
    defaultPayload: undefined,
  };
}

export function registrationInstance(accountId: string): string {
  return `acct-${fnv1aHex(accountId)}`;
}

function fnv1aHex(value: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

export class AndroidDedupCache {
  private seen = new Map<string, true>();

  constructor(readonly max = 100) {}

  mark(key: string): boolean {
    if (this.seen.has(key)) {
      this.seen.delete(key);
      this.seen.set(key, true);
      return false;
    }
    this.seen.set(key, true);
    while (this.seen.size > this.max) {
      const oldest = this.seen.keys().next().value;
      if (oldest === undefined) break;
      this.seen.delete(oldest);
    }
    return true;
  }

  get size(): number {
    return this.seen.size;
  }
}

export function dedupKey(
  accountId: string,
  instance: string,
  roomId: string,
  eventId: string,
): string {
  return `${accountId}\u0000${instance}\u0000${roomId}\u0000${eventId}`;
}

async function safeUnregister(
  unregisterPusher: (appId: string, pushkey: string) => Promise<void>,
  pusher: RegisteredAndroidPusher,
): Promise<void> {
  try {
    await unregisterPusher(pusher.appId, pusher.pushkey);
  } catch {
    return;
  }
}

export interface AndroidPushSessionPorts {
  requestRegistration: () => Promise<AndroidPushRegistrationOutcome>;
  registerPusher: (config: PusherConfig) => Promise<void>;
  unregisterPusher: (appId: string, pushkey: string) => Promise<void>;
  allowedHosts: ReadonlySet<string>;
  currentInstance: () => string;
}

export class AndroidPushSession {
  private supported = true;
  private signingOut = false;
  private ended = false;
  private registered: RegisteredAndroidPusher | null = null;
  private settle: Promise<unknown> | null = null;
  private retire: Promise<void> | null = null;

  constructor(private readonly ports: AndroidPushSessionPorts) {}

  get isSupported(): boolean {
    return this.supported;
  }

  get registeredPusher(): RegisteredAndroidPusher | null {
    return this.registered;
  }

  get isSigningOut(): boolean {
    return this.signingOut;
  }

  async attempt(): Promise<AndroidAttemptOutcome> {
    if (!this.supported) return "unsupported";
    if (this.ended || this.signingOut) return "cancelled";
    const outcome = await this.ports.requestRegistration();
    if (this.ended || this.signingOut) return "cancelled";
    switch (outcome) {
      case "unsupported":
        this.supported = false;
        return "unsupported";
      case "denied":
        return "denied";
      case "noDistributor":
        return "noDistributor";
      case "network":
        return "network";
      case "registered":
        return "registered";
    }
  }

  private isCurrentInstance(instance: string): boolean {
    const current = this.ports.currentInstance();
    return current !== "" && instance === current;
  }

  onEndpoint(endpoint: PlatformAndroidPushEndpoint): Promise<void> {
    if (this.ended || this.signingOut) return Promise.resolve();
    if (!this.isCurrentInstance(endpoint.instance)) return Promise.resolve();
    const config = buildAndroidPusherConfig(endpoint.endpoint, this.ports.allowedHosts);
    if (!config) return Promise.resolve();
    const settle = this.registerWith(config, endpoint.instance);
    this.settle = settle;
    return settle;
  }

  private async registerWith(config: PusherConfig, instance: string): Promise<void> {
    let ok = false;
    try {
      await this.ports.registerPusher(config);
      ok = true;
    } catch {
      ok = false;
    }
    if (!ok) return;
    if (this.ended || this.signingOut) return;
    const previous = this.registered;
    this.registered = {
      instance,
      appId: config.appId,
      pushkey: config.pushkey,
    };
    this.settle = null;
    if (previous && previous.pushkey !== config.pushkey) this.retirePrevious(previous);
  }

  private retirePrevious(previous: RegisteredAndroidPusher): void {
    const done = safeUnregister(this.ports.unregisterPusher, previous);
    this.retire = done;
    void done.then(() => {
      if (this.retire === done) this.retire = null;
    });
  }

  async signOut(): Promise<void> {
    this.signingOut = true;
    if (this.settle) await this.settle.catch(() => undefined);
    if (this.retire) await this.retire.catch(() => undefined);
    const current = this.registered;
    this.registered = null;
    if (current) await safeUnregister(this.ports.unregisterPusher, current);
    this.ended = true;
  }

  abort(): void {
    this.signingOut = true;
    this.ended = true;
  }
}

export function useAndroidSignedInLatch(state: ClientState, userId: string | null): boolean {
  const [signedIn, setSignedIn] = useState(false);
  useEffect(() => {
    if (userId === null) setSignedIn(false);
    else if (state === "synced") setSignedIn(true);
  }, [state, userId]);
  return signedIn;
}

export interface UseAndroidPushRegistrationArgs {
  client: Client | null;
  signedIn: boolean;
  onNotificationTap: (roomId: string) => void;
  registerSignOutTeardown?: (teardown: () => Promise<void> | void) => () => void;
  accountId?: string | null;
  allowedHosts?: ReadonlySet<string>;
}

export function useAndroidPushRegistration({
  client,
  signedIn,
  onNotificationTap,
  registerSignOutTeardown,
  accountId,
  allowedHosts = NURION_NTFY_HOSTS,
}: UseAndroidPushRegistrationArgs): void {
  const sessionRef = useRef<AndroidPushSession | null>(null);
  const signingOutRef = useRef(false);
  const inFlightRef = useRef(false);
  const rearmRequestedRef = useRef(false);
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const runCycleRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    if (!client) return;
    signingOutRef.current = false;
    inFlightRef.current = false;
    rearmRequestedRef.current = false;
    for (const id of timersRef.current) clearTimeout(id);
    timersRef.current.clear();

    sessionRef.current = new AndroidPushSession({
      requestRegistration: async () => {
        try {
          return await platform.requestAndroidPushRegistration();
        } catch {
          return "unsupported";
        }
      },
      registerPusher: (config) => client.bindings.registerPusher(config),
      unregisterPusher: (appId, pushkey) => client.bindings.unregisterPusher(appId, pushkey),
      allowedHosts,
      currentInstance: () => {
        if (signingOutRef.current) return "";
        const account = accountId || client.bindings.getUserId() || "";
        return account ? registrationInstance(account) : "";
      },
    });

    let endpointUnlisten = () => {};
    void Promise.resolve()
      .then(() =>
        platform.onAndroidPushEndpoint((endpoint) => {
          void sessionRef.current?.onEndpoint(endpoint);
        }),
      )
      .then((fn) => {
        endpointUnlisten = fn;
      })
      .catch(() => {});

    const teardownUnlisten = registerSignOutTeardown?.(async () => {
      signingOutRef.current = true;
      await sessionRef.current?.signOut();
    });

    return () => {
      endpointUnlisten();
      teardownUnlisten?.();
      for (const id of timersRef.current) clearTimeout(id);
      timersRef.current.clear();
      sessionRef.current?.abort();
    };
  }, [client, registerSignOutTeardown, accountId, allowedHosts]);

  useEffect(() => {
    if (!client || !signedIn) return;
    signingOutRef.current = false;
    let alive = true;
    let delayIndex = -1;

    const close = () => {
      inFlightRef.current = false;
      if (alive && rearmRequestedRef.current && !signingOutRef.current) {
        rearmRequestedRef.current = false;
        runCycleRef.current?.();
      }
    };

    const scheduleNext = () => {
      if (delayIndex >= RETRY_DELAYS_MS.length - 1) {
        close();
        return;
      }
      delayIndex += 1;
      const id = setTimeout(step, RETRY_DELAYS_MS[delayIndex]);
      timersRef.current.add(id);
    };

    const step = () => {
      if (!alive || signingOutRef.current) return close();
      const session = sessionRef.current;
      void session
        ?.attempt()
        .then((outcome) => {
          if (!alive || signingOutRef.current) return close();
          if (isRetryable(outcome)) {
            scheduleNext();
            return;
          }
          close();
        })
        .catch(() => close());
    };

    const runCycle = () => {
      if (!alive || signingOutRef.current) {
        rearmRequestedRef.current = false;
        return;
      }
      if (inFlightRef.current) {
        rearmRequestedRef.current = true;
        return;
      }
      inFlightRef.current = true;
      delayIndex = -1;
      step();
    };

    runCycleRef.current = runCycle;
    runCycle();

    return () => {
      alive = false;
      inFlightRef.current = false;
      rearmRequestedRef.current = false;
      for (const id of timersRef.current) clearTimeout(id);
      timersRef.current.clear();
      runCycleRef.current = undefined;
    };
  }, [client, signedIn]);

  useEffect(() => {
    if (!client) return;
    const onForeground = () => void runCycleRef.current?.();
    const onVisibility = () => {
      if (document.visibilityState === "visible") onForeground();
    };
    window.addEventListener("online", onForeground);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("online", onForeground);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [client]);

  useEffect(() => {
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const drain = () => {
      void Promise.resolve()
        .then(() => platform.takeAndroidPushTap())
        .then((roomId) => {
          if (!cancelled && roomId) onNotificationTap(roomId);
        })
        .catch(() => {});
    };
    drain();
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      drain();
      if (retry !== undefined) clearTimeout(retry);
      retry = setTimeout(drain, 400);
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      cancelled = true;
      if (retry !== undefined) clearTimeout(retry);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [onNotificationTap]);
}
